import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Tooltip } from 'antd';
import { FiEdit3, FiXCircle } from 'react-icons/fi';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { useAnnotationActiveNode } from '@routes/userRoutes/projects/singleProject/components/sidebars/annotation/useAnnotationActiveNode';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';
import { getGrid, getGridFeatures } from '@utils/api';
import { ANNOTATION_PHASE, ANNOTATION_TARGET, MODEL_TYPE, PROJECT_MODE } from '@utils/constants';
import { filterGridByPolygon } from '@utils/map/freeHand.draw';
import { ANNOTATION_SEARCH_PARAMS, decodeArea, encodeArea } from '@utils/projectPath';

import AnnotateOrthophoto from '../ai/AnnotateOrthophoto';
import AnnotateSingleImages from '../ai/AnnotateSingleImages';
import MapLoadingIndicator from '../MapLoadingIndicator';
import { useAnnotationBoundaryDraw } from './useAnnotationBoundaryDraw';
import { useTrainingPermission } from './useTrainingPermission';

const annotationSidebarId = 'annotationSidebar';
const { node: NODE_PARAM, area: AREA_PARAM } = ANNOTATION_SEARCH_PARAMS;

/**
 * Collection view's way into annotation mode. "Annotate to improve AI" opens the
 * object tree with the first object that has a model selected and starts the
 * boundary draw at once (PICK: the tree stays open, so another object can be
 * picked while the area is drawn). Once the area is in, the tiles of the object
 * active at that moment are annotated (ANNOTATE).
 *
 * The flow is in the URL as well (`annotate` = the object, `area` = the drawn
 * boundary), so a reload or a shared link lands in it: with both, straight in the
 * session; with the object alone, in the pick with the area still to be drawn.
 */
const AnnotateObjects = () => {
	const {
		project,
		projectMode,
		toolBarVisible,
		isDemo,
		mapObject,
		annotationMode,
		selectedSingleImage,
		filteredImages,
		setActiveSidebar,
		removeFromActiveSidebars,
		dispatch,
	} = useProject();
	const { addToast } = useToast();

	const { node: activeNode, defaultNode, nodes, isLoading } = useAnnotationActiveNode();
	const checkPermission = useTrainingPermission();
	const { start, cancel, resume, isDrawing, layersToShow } = useAnnotationBoundaryDraw();
	// Only the entry is gated: cancelling must stay reachable once the flow owns the map.
	const { busy: mapBusy, reason: blockedReason, getBusy } = useMapToolBusy();

	// True from the click until the draw step is live on the map.
	const [fetching, setFetching] = useState(false);

	const phase = annotationMode?.target === ANNOTATION_TARGET.OBJECT ? annotationMode.phase : null;
	const isPicking = phase === ANNOTATION_PHASE.PICK;

	// The grid fetch and the draw resolve after renders; the ready callback must
	// see the phase and the active object as they are then, not as at the click.
	const phaseRef = useRef(phase);
	phaseRef.current = phase;
	const activeNodeRef = useRef(activeNode);
	activeNodeRef.current = activeNode;
	const finishingRef = useRef(false);

	const leavePick = () => {
		dispatch({ type: 'setAnnotationMode', payload: null });
		removeFromActiveSidebars(annotationSidebarId);
	};

	const enterAnnotate = (tiles, boundary, { startTile = null } = {}) => {
		dispatch({ type: 'setModelType', payload: MODEL_TYPE.OBJECT_DETECTION });
		dispatch({
			type: 'setAnnotationMode',
			payload: {
				target: ANNOTATION_TARGET.OBJECT,
				phase: ANNOTATION_PHASE.ANNOTATE,
				gridFeatures: tiles,
				// Kept so a switch to another object cuts its grid to the same area.
				boundary,
				// The image the session opens on (an image opened from the slider).
				...(startTile ? { startTile } : {}),
			},
		});
	};

	// The area is drawn: start on the object active now. Its grid was fetched
	// before the draw unless the pick changed meanwhile, in which case the new
	// object's grid is cut to the same area first.
	const onBoundaryReady = async (fetchedFor, tiles, boundary) => {
		if (phaseRef.current !== ANNOTATION_PHASE.PICK) return;
		const node = activeNodeRef.current;
		if (!node) return leavePick();

		// The draw is over but the session has not started: not a cancel, so the
		// effect below must leave the sidebar alone while another object's grid loads.
		finishingRef.current = true;
		try {
			const store = useAnnotationSessionStore.getState();
			let ownTiles = tiles;
			if (node.uuid !== fetchedFor.uuid) {
				let grid;
				try {
					const response = await getGrid({
						project_uuid: project.uuid,
						node_uuid: node.uuid,
					});
					grid = response?.grid_geojson?.features ?? [];
				} catch (error) {
					if (phaseRef.current !== ANNOTATION_PHASE.PICK) return;
					// A failed request is not "no tiles in the area".
					addToast({
						id: 'annotation-grid-failed',
						bg: 'danger',
						title: `Could not load the tiles of ${node.name}. Please try again.`,
						message: error?.message,
					});
					return leavePick();
				}
				// The flow was left (or cancelled) while the grid loaded.
				if (phaseRef.current !== ANNOTATION_PHASE.PICK) return;
				ownTiles = boundary ? filterGridByPolygon(grid, boundary) : grid;
				if (ownTiles.length === 0) {
					addToast({
						id: 'annotation-no-tiles',
						bg: 'warning',
						title: `${node.name} has no tiles inside the drawn area.`,
					});
					return leavePick();
				}
			}
			// Cached so a switch back later needs no fetch.
			store.setTiles(fetchedFor.uuid, tiles);
			store.setTiles(node.uuid, ownTiles);
			enterAnnotate(ownTiles, boundary);
		} finally {
			finishingRef.current = false;
		}
	};

	const drawBoundaryFor = async node => {
		setFetching(true);
		try {
			const live = await start({
				fetchGrid: () =>
					getGridFeatures({ project_uuid: project.uuid, node_uuid: node.uuid }),
				onReady: (tiles, { boundary = null } = {}) =>
					onBoundaryReady(node, tiles, boundary),
			});
			// No grid, so no draw: leave the pick (and its URL) instead of waiting in it.
			if (live === false && phaseRef.current === ANNOTATION_PHASE.PICK) leavePick();
		} finally {
			setFetching(false);
		}
	};

	// The session store (tiles cut to the area per object, saved boxes, labels) is
	// one session's; a new flow on the same page must not inherit the last one's
	// tiles, or a switch to an object annotated before shows the old area.
	const freshSession = () => useAnnotationSessionStore.getState().reset();

	/** Open the pick on `node` (null: the first object with a model) and start the area draw. */
	const beginPick = node => {
		freshSession();
		dispatch({ type: 'setAnnotationActiveNodeUuid', payload: node?.uuid ?? null });

		// No model classes: the sidebar shows the object tree alone.
		dispatch({ type: 'setAnnotationSidebarData', payload: null });
		dispatch({
			type: 'setAnnotationMode',
			payload: {
				target: ANNOTATION_TARGET.OBJECT,
				phase: ANNOTATION_PHASE.PICK,
				gridFeatures: [],
			},
		});
		setActiveSidebar({ sidebarId: annotationSidebarId });
		drawBoundaryFor(node ?? defaultNode);
	};

	const begin = () => {
		if (!defaultNode || !checkPermission()) return;
		// Every session starts on the first object with a model, whatever was picked last time.
		beginPick(null);
	};

	// An image is open in a single-image project: annotate from that image. There is
	// no area to draw: the session takes the slider's images (its order, sort and
	// filters) and opens on the opened one, so Previous / Next reach the others. It
	// is not put in the URL (a link without an area would start the area draw
	// instead): a reload returns to the opened image, not annotating.
	const openedImageName = selectedSingleImage?.getProperties?.()?.name;
	const isOpenImageEntry =
		Boolean(selectedSingleImage) && projectMode === PROJECT_MODE.SINGLE_IMAGE;

	const beginOpenImage = async () => {
		if (!defaultNode || !checkPermission()) return;

		setFetching(true);
		try {
			const grid = await getGridFeatures({
				project_uuid: project.uuid,
				node_uuid: defaultNode.uuid,
			});
			const byName = new Map(grid?.map(tile => [tile?.properties?.tile_name, tile]) ?? []);
			const feature = byName.get(openedImageName);
			if (!feature) {
				addToast({
					id: 'start-annotation-error',
					bg: 'danger',
					title: 'An error occurred and annotation could not be started. Please try again later.',
				});
				return;
			}

			// The grid request gave a boundary edit or an area draw time to take the map over.
			const { busy, reason } = getBusy();
			if (busy) {
				addToast({ id: 'annotation-map-busy', bg: 'warning', title: reason });
				return;
			}

			freshSession();
			dispatch({ type: 'setAnnotationActiveNodeUuid', payload: null });
			// No model classes: the sidebar shows the object tree alone.
			dispatch({ type: 'setAnnotationSidebarData', payload: null });
			// Hides the layers and the map toolbar; the annotator shows them again on exit.
			resume();
			// Every image the slider lists, in its order (its sort and filters), so
			// Previous / Next step through them; the session opens on the opened image.
			const listed = (filteredImages ?? [])
				.map(image => byName.get(image?.get?.('name')))
				.filter(Boolean);
			const images = listed.includes(feature) ? listed : [feature, ...listed];
			useAnnotationSessionStore.getState().setTiles(defaultNode.uuid, images);
			enterAnnotate(images, null, { startTile: openedImageName });
			setActiveSidebar({ sidebarId: annotationSidebarId });
		} finally {
			setFetching(false);
		}
	};

	// Cancelling the draw (toolbar button or the draw hook's own ESC) leaves the flow.
	const abort = () => {
		cancel();
		leavePick();
	};
	const drawLiveRef = useRef(false);
	useEffect(() => {
		if (isDrawing) drawLiveRef.current = true;
		else if (drawLiveRef.current && isPicking && !fetching && !finishingRef.current) {
			drawLiveRef.current = false;
			leavePick();
		}
		if (!isPicking) drawLiveRef.current = false;
		// leavePick dispatches with stable callbacks.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDrawing, isPicking, fetching]);

	// The flow in the URL: the object while the pick or the session runs, the area
	// once the session runs. Rewritten in place (no history entry per change) and
	// dropped when the flow ends. The opened-image session stays out of it.
	const [searchParams, setSearchParams] = useSearchParams();
	const urlNode = searchParams.get(NODE_PARAM);
	const urlArea = searchParams.get(AREA_PARAM);

	const setUrlFlow = (nodeUuid, area) =>
		setSearchParams(
			params => {
				const next = new URLSearchParams(params);
				if (nodeUuid) next.set(NODE_PARAM, nodeUuid);
				else next.delete(NODE_PARAM);
				if (area) next.set(AREA_PARAM, area);
				else next.delete(AREA_PARAM);
				return next;
			},
			{ replace: true }
		);

	// The link the follower below has acted on (or that this component wrote
	// itself): a running flow must not be followed back into once it ends, while
	// its params are still in the URL.
	const handledLinkRef = useRef(null);
	const linkOf = (nodeUuid, area) => (nodeUuid ? `${nodeUuid}|${area ?? ''}` : null);

	const flowSeenRef = useRef(false);
	useEffect(() => {
		if (phase && selectedSingleImage) return;
		if (phase && activeNode) {
			flowSeenRef.current = true;
			const area =
				phase === ANNOTATION_PHASE.ANNOTATE ? encodeArea(annotationMode?.boundary) : null;
			handledLinkRef.current = linkOf(activeNode.uuid, area);
			if (urlNode !== activeNode.uuid || urlArea !== area) setUrlFlow(activeNode.uuid, area);
		} else if (!phase && flowSeenRef.current) {
			flowSeenRef.current = false;
			if (urlNode || urlArea) setUrlFlow(null, null);
		}
		// setUrlFlow wraps the router's stable setter.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phase, activeNode, annotationMode?.boundary, selectedSingleImage, urlNode, urlArea]);

	// A link (or a reload) with the flow in it: rebuild the session from the object's
	// grid cut to the area, or open the pick on the object when there is no area yet.
	// Saved boxes come back through the annotation list; unsaved ones are gone.
	const [restoring, setRestoring] = useState(false);

	const restoreSession = async (node, boundary) => {
		setRestoring(true);
		try {
			const grid = await getGridFeatures({
				project_uuid: project.uuid,
				node_uuid: node.uuid,
			});
			const tiles = filterGridByPolygon(grid, boundary);
			if (tiles.length === 0) {
				setUrlFlow(null, null);
				addToast({
					id: 'annotation-resume-failed',
					bg: 'warning',
					title: 'The annotation session could not be restored. Start it again.',
				});
				return;
			}

			freshSession();
			dispatch({ type: 'setAnnotationActiveNodeUuid', payload: node.uuid });
			// No model classes: the sidebar shows the object tree alone.
			dispatch({ type: 'setAnnotationSidebarData', payload: null });
			resume();
			useAnnotationSessionStore.getState().setTiles(node.uuid, tiles);
			enterAnnotate(tiles, boundary);
			setActiveSidebar({ sidebarId: annotationSidebarId });
		} finally {
			setRestoring(false);
		}
	};

	useEffect(() => {
		// Once per link, when the map and the taxonomy are in and nothing else runs.
		const link = linkOf(urlNode, urlArea);
		if (!link) {
			// A flow that is running has just written its link; it is handled.
			if (!phase) handledLinkRef.current = null;
			return;
		}
		if (handledLinkRef.current === link) return;
		if (!mapObject || !project?.uuid || isLoading || !nodes?.length) return;
		if (annotationMode || selectedSingleImage || restoring) return;
		// Another tool owns the map (a boundary edit, an area draw, measuring): the
		// link waits and is followed once it is done, as the button would be enabled.
		if (mapBusy) return;
		handledLinkRef.current = link;

		// The button is off in a demo project; a link must not open what it would not.
		if (isDemo) {
			addToast({
				id: 'annotation-link-demo',
				bg: 'warning',
				title: 'Annotation is not available in a demo project.',
			});
			setUrlFlow(null, null);
			return;
		}

		const node = nodes.find(candidate => candidate.uuid === urlNode);
		if (!node?.is_available) {
			addToast({
				id: 'annotation-link-object',
				bg: 'warning',
				title: node
					? `${node.name} has no model for this project type.`
					: 'The object in the link does not exist.',
			});
			setUrlFlow(null, null);
			return;
		}
		if (!checkPermission()) {
			setUrlFlow(null, null);
			return;
		}
		if (urlArea === null) {
			beginPick(node);
			return;
		}
		const boundary = decodeArea(urlArea);
		if (!boundary) {
			addToast({
				id: 'annotation-link-area',
				bg: 'warning',
				title: 'The area in the link could not be read. Draw it again.',
			});
			// The object stays: the link re-enters here as a pick.
			setUrlFlow(node.uuid, null);
			return;
		}
		restoreSession(node, boundary);
		// Runs once per link; the handlers read the latest state at call time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		urlNode,
		urlArea,
		mapObject,
		project?.uuid,
		isLoading,
		nodes,
		annotationMode,
		selectedSingleImage,
		restoring,
		mapBusy,
		isDemo,
	]);

	if (!mapObject) return null;
	// An opened image offers the annotation only in a single-image project.
	if (selectedSingleImage && !isOpenImageEntry) return null;

	if (phase === ANNOTATION_PHASE.ANNOTATE) {
		if (projectMode === PROJECT_MODE.ORTHOPHOTO) {
			return (
				<AnnotateOrthophoto
					layersToShow={layersToShow}
					hideAnnotationSidebar={() => removeFromActiveSidebars(annotationSidebarId)}
				/>
			);
		}
		if (projectMode === PROJECT_MODE.SINGLE_IMAGE) {
			return <AnnotateSingleImages layersToShow={layersToShow} />;
		}
	}

	if (isPicking) {
		return (
			<Button icon={<FiXCircle />} onClick={abort} loading={fetching} disabled={isDemo}>
				Cancel annotation
			</Button>
		);
	}

	const noObject = !isLoading && !defaultNode;
	const tooltipTitle =
		blockedReason ||
		(noObject
			? 'No object has a model for this project type yet'
			: isOpenImageEntry
				? 'Annotate this image; the first object with a model is picked for you'
				: 'Draw the area to annotate; the first object with a model is picked for you');

	return (
		<>
			{restoring && (
				<MapLoadingIndicator
					message="Restoring the annotation session"
					hint="this can take a moment"
				/>
			)}
			<Tooltip title={tooltipTitle}>
				{/* antd removes pointer events from disabled buttons, so the wrapper
				    keeps the tooltip hoverable while the button is disabled. */}
				<span>
					<Button
						icon={<FiEdit3 />}
						onClick={isOpenImageEntry ? beginOpenImage : begin}
						loading={isOpenImageEntry && fetching}
						disabled={
							// An opened image hides the map toolbar (`toolBarVisible` false)
							// for as long as it is open, so that flag cannot gate this entry.
							(!toolBarVisible && !isOpenImageEntry) ||
							isDemo ||
							mapBusy ||
							isLoading ||
							noObject ||
							restoring
						}>
						{isOpenImageEntry ? 'Annotate this image' : 'Annotate to improve AI'}
					</Button>
				</span>
			</Tooltip>
		</>
	);
};

export default AnnotateObjects;
